import {createEstablishedAhpSessionBootstrap} from '@github-ui/ahp-session-client/bootstrap'
import type {
  EstablishedAhpPlanningPolicy,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpSessionProvisioned,
  EstablishRequest,
  ProvisionRequest,
} from '@github-ui/ahp-session-client/contracts'
import type {AcquireRelayLeaseOptions} from '@github-ui/ahp-relay/relay-registry'

export interface MissionControlAhpSessionRef {
  taskId: string
  environmentId: string
  sessionId: string
  channel?: string
}

type MissionControlRelayOptions = Omit<AcquireRelayLeaseOptions, 'environmentId' | 'sessionId' | 'channel' | 'taskId'> &
  Partial<Pick<AcquireRelayLeaseOptions, 'channel' | 'taskId'>>
type MissionControlAdapterRequest = Exclude<EstablishRequest, {mode: 'resume-provision'}>

export interface MissionControlEstablishedAhpBootstrapOptions {
  provisionSession(input: ProvisionRequest): Promise<MissionControlAhpSessionRef>
  createRelayOptions(
    identity: EstablishedAhpSessionIdentity,
    request?: MissionControlAdapterRequest,
  ): MissionControlRelayOptions
  attachSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  wakeSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  recoverSession?(identity: EstablishedAhpSessionIdentity): Promise<MissionControlAhpSessionRef>
  planning?: EstablishedAhpPlanningPolicy
}

function identityFor(session: MissionControlAhpSessionRef): EstablishedAhpSessionIdentity {
  return {
    taskId: session.taskId,
    environmentId: session.environmentId,
    sessionId: session.sessionId,
    ...(session.channel ? {channel: session.channel} : {}),
  }
}

function sessionForIdentity(identity: EstablishedAhpSessionIdentity): MissionControlAhpSessionRef {
  return {
    taskId: identity.taskId ?? identity.sessionId,
    environmentId: identity.environmentId,
    sessionId: identity.sessionId,
    ...(identity.channel ? {channel: identity.channel} : {}),
  }
}

function provisioned(
  session: MissionControlAhpSessionRef,
  createRelayOptions: MissionControlEstablishedAhpBootstrapOptions['createRelayOptions'],
  request: MissionControlAdapterRequest,
): EstablishedAhpSessionProvisioned {
  const identity = identityFor(session)
  return {
    identity,
    relay: {
      ...createRelayOptions(identity, request),
      environmentId: identity.environmentId,
      sessionId: identity.sessionId,
      ...(identity.channel ? {channel: identity.channel} : {}),
      ...(identity.taskId ? {taskId: identity.taskId} : {}),
    },
  }
}

/**
 * Mission Control owns task eligibility, history, wakeability, and token
 * wiring. Once it resolves a live AHP identity, this constructor hands it to
 * the same established-session bootstrap used by Quick Chat.
 */
export function createMissionControlEstablishedAhpBootstrap(
  options: MissionControlEstablishedAhpBootstrapOptions,
): EstablishedAhpSessionBootstrap {
  return createEstablishedAhpSessionBootstrap(
    {
      provision: async input =>
        provisioned(await options.provisionSession(input), options.createRelayOptions, {mode: 'provision', input}),
      attach: async identity =>
        provisioned(
          await (options.attachSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'attach', identity},
        ),
      wake: async identity =>
        provisioned(
          await (options.wakeSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'wake', identity},
        ),
      recover: async identity =>
        provisioned(
          await (options.recoverSession?.(identity) ?? Promise.resolve(sessionForIdentity(identity))),
          options.createRelayOptions,
          {mode: 'recover', identity},
        ),
    },
    options.planning,
  )
}
